import React from 'react';

import styled from '@emotion/styled';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import { CAPTURE_TOLERANCE_MULTIPLIER, MIN_CAPTURE_AMOUNT } from './constants';

// Add a 700ms delay to the tooltip appearance
const DelayedTooltip = styled(Tooltip)`
  .cr-tooltip {
    transition-delay: 0.7s;
  }
`;

interface PreAuthLineRowProps {
  line: SaleLine;
  captureAmount: number;
  isSelected: boolean;
  isTotalBelowMinimum: boolean;
  onCheckboxChange: (lineId: string, checked: boolean) => void;
  onAmountChange: (lineId: string, amount: number) => void;
  onValidationChange: (lineId: string, isValid: boolean) => void;
}

const PreAuthLineRow: React.FC<PreAuthLineRowProps> = ({
  line,
  captureAmount,
  isSelected,
  isTotalBelowMinimum,
  onCheckboxChange,
  onAmountChange,
  onValidationChange,
}) => {
  const calcTotal = parseFloat(line.calcTotal);
  const maxAllowedAmount = calcTotal * CAPTURE_TOLERANCE_MULTIPLIER;
  const hasMaxError = isSelected && captureAmount > maxAllowedAmount;
  const hasMinError = isSelected && !hasMaxError && isTotalBelowMinimum;
  const hasError = hasMaxError || hasMinError;

  // Track if the input is focused and the raw input value during editing
  const [isFocused, setIsFocused] = React.useState(false);
  const [editingValue, setEditingValue] = React.useState('');

  // Reset focus state when input becomes disabled
  React.useEffect(() => {
    if (!isSelected && isFocused) {
      setIsFocused(false);
    }
  }, [isSelected, isFocused]);

  // Sync editingValue when captureAmount changes externally (e.g., from checkbox)
  // but only when not actively editing
  React.useEffect(() => {
    if (!isFocused) {
      setEditingValue(captureAmount.toFixed(2));
    }
  }, [captureAmount, isFocused]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    onCheckboxChange(line.saleLineID, checked);

    // Validate when checkbox changes
    if (checked) {
      const isValid = captureAmount <= maxAllowedAmount;
      onValidationChange(line.saleLineID, isValid);
    } else {
      onValidationChange(line.saleLineID, true);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Store the raw value for display while editing
    setEditingValue(rawValue);

    // Parse and update the parent state immediately
    const numericValue = parseFloat(rawValue) || 0;
    onAmountChange(line.saleLineID, numericValue);

    // Validate the amount
    const isValid = numericValue <= maxAllowedAmount;
    onValidationChange(line.saleLineID, isValid);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Initialize editing value with current amount
    setEditingValue(captureAmount.toString());
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Clear the editing value - we'll use the formatted captureAmount from props
    setEditingValue('');
  };

  const discount = line.Discount?.discountAmount ? parseFloat(line.Discount.discountAmount) : 0;

  // Display raw editing value when focused, formatted value when not focused
  const displayValue = isFocused ? editingValue : captureAmount.toFixed(2);

  const input = (
    <div className="money-field-container">
      <input
        type="text"
        className="money"
        value={displayValue}
        onChange={handleAmountChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={!isSelected}
        data-test="capture-input"
        data-status={hasError ? 'error' : undefined}
        style={{
          textAlign: 'right',
          border: hasError ? '1px solid #de2e37' : undefined,
          backgroundColor: hasError ? '#fff5f5' : undefined,
        }}
      />
    </div>
  );

  // Determine which tooltip to show (if any)
  let wrappedInput = input;
  if (hasMaxError) {
    // Error state: immediate tooltip with error message
    wrappedInput = (
      <Tooltip
        content={`Maximum capture: $${maxAllowedAmount.toFixed(2)}. Reduce amount.`}
        placement="top"
      >
        {input}
      </Tooltip>
    );
  } else if (hasMinError) {
    wrappedInput = (
      <Tooltip
        content={`Total capture amount must be greater than $${MIN_CAPTURE_AMOUNT.toFixed(2)}`}
        placement="top"
      >
        {input}
      </Tooltip>
    );
  } else if (!isSelected) {
    // Not selected state: delayed tooltip with instruction message
    wrappedInput = (
      <DelayedTooltip content="Select the item to edit amount" placement="top">
        {input}
      </DelayedTooltip>
    );
  }

  return (
    <tr className="preauth-line-row" data-line-id={line.saleLineID}>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          className="preauth-line-checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          title="Select to capture full amount"
          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        />
      </td>
      <td>
        <span>{line.Item?.description}</span>
      </td>
      <td className="money">${parseFloat(line.unitPrice).toFixed(2)}</td>
      <td className="center">{parseFloat(line.unitQuantity).toFixed(0)}</td>
      <td className="money">${discount.toFixed(2)}</td>
      <td className="center">{line.tax === 'true' ? 'Yes' : 'No'}</td>
      <td className="money">${calcTotal.toFixed(2)}</td>
      <td className="money">{wrappedInput}</td>
    </tr>
  );
};

export default PreAuthLineRow;
